import { Box, Group, Indicator, Text, UnstyledButton } from "@mantine/core";
import type { ReactNode } from "react";

export interface NavItem {
  key: string;
  label: string;
  icon: ReactNode;
  /** A dot on the icon: this module has something waiting. */
  alert?: boolean;
  /**
   * The dot's colour, which the brief asks to be the module's own.
   *
   * Red for everything would say "wrong" about a queue that is simply a
   * queue, and a phone nav has no room for a number - so the dot's job is
   * only to say WHERE, and the module's colour is what says it.
   */
  alertColor?: string;
}

/**
 * The phone's module nav.
 *
 * Presentational on purpose: it takes its items as a prop and knows nothing
 * about routes or roles. Phase 2 wires it to the modules the signed-in user
 * actually holds - a nav that hard-coded six modules would offer a Payment
 * Collection Agent a Service tab that refuses them, and there is already a
 * test asserting the app does not do that.
 *
 * Five visible plus "More", at the height the theme sets, with the safe-area
 * inset below it.
 */
export function BottomNav({
  items,
  active,
  onSelect,
  moreLabel = "More",
  onMore,
}: {
  items: NavItem[];
  active?: string;
  onSelect: (key: string) => void;
  moreLabel?: string;
  onMore?: () => void;
}) {
  const visible = items.slice(0, onMore ? 5 : 6);

  return (
    <Box
      component="nav"
      aria-label="Modules"
      className="safe-bottom"
      style={{
        position: "sticky",
        bottom: 0,
        background: "var(--mantine-color-white)",
        borderTop: "1px solid var(--mantine-color-gray-3)",
        zIndex: 3,
        // The theme says 58px and that is the whole nav, border included -
        // border-box, or the 1px rule makes it 59 and the occlusion padding
        // every screen sets from the same token is one pixel short.
        height: "var(--supplyx-bottom-nav-height, 58px)",
        boxSizing: "border-box",
      }}
    >
      <Group gap={0} grow h="100%" wrap="nowrap">
        {visible.map((item) => (
          <NavButton
            key={item.key}
            item={item}
            active={active === item.key}
            onClick={() => onSelect(item.key)}
          />
        ))}
        {onMore && (
          <NavButton
            item={{ key: "__more", label: moreLabel, icon: <Text fz="lg">⋯</Text> }}
            active={false}
            onClick={onMore}
          />
        )}
      </Group>
    </Box>
  );
}

function NavButton({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  const body = (
    <UnstyledButton
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        // A flex item's min-width is `auto`, so a button will NOT shrink below
        // its label's min-content width however hard the row is squeezed: six
        // tabs of un-shrinkable text is a nav wider than the phone, and a page
        // wider than the phone is what makes a phone look zoomed in. Zero
        // here plus truncate below means the row always fits and a long label
        // gives way instead.
        minWidth: 0,
        color: active
          ? "var(--mantine-color-indigo-7)"
          : "var(--mantine-color-gray-6)",
      }}
    >
      {item.icon}
      <Text
        fz="xs"
        fw={active ? 600 : 400}
        truncate
        style={{ color: "inherit", maxWidth: "100%" }}
      >
        {item.label}
      </Text>
    </UnstyledButton>
  );

  // When there is a dot, the INDICATOR is what `Group grow` puts its flex: 1
  // on, not the button - so the same min-width: 0 has to be here too, or a
  // tab with something waiting is the one that refuses to shrink. Its root is
  // inline-block by default, which would not fill the slot either.
  return item.alert ? (
    <Indicator
      size={7}
      offset={8}
      position="top-end"
      color={item.alertColor ?? "red"}
      styles={{
        root: { display: "block", minWidth: 0, height: "100%" },
        ...(item.alertColor?.startsWith("#")
          ? { indicator: { background: item.alertColor } }
          : {}),
      }}
    >
      {body}
    </Indicator>
  ) : (
    body
  );
}
